<template>
  <div class="container-fluid">
    <div class="mb-4">
      <h5 class="mb-0 font-weight-bold page-title">Automation</h5>
      <p class="text-secondary text-sm mb-0">
        Track automated test runs across multiple repositories (Playwright, Cypress, Appium)
      </p>
    </div>

    <div v-if="loading" class="text-center py-5">
      <i class="fas fa-spinner fa-spin fa-lg text-secondary"></i>
      <p class="text-secondary text-sm mt-2 mb-0">Loading automation data...</p>
    </div>

    <div v-else-if="loadError" class="text-center py-5">
      <i class="fas fa-exclamation-triangle fa-2x text-warning opacity-6"></i>
      <p class="text-secondary text-sm mt-3 mb-2">{{ loadError }}</p>
      <button type="button" class="btn btn-sm btn-outline-primary mb-0" @click="loadAll">
        Retry
      </button>
    </div>

    <template v-else>
      <div class="stats-row mb-4">
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-total">
              <i class="fas fa-code-branch"></i>
            </div>
            <div>
              <p class="stat-label">Repositories</p>
              <h5 class="stat-value">{{ summary.repoCount }}</h5>
            </div>
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-inprogress">
              <i class="fas fa-spinner"></i>
            </div>
            <div>
              <p class="stat-label">Running</p>
              <h5 class="stat-value">{{ summary.runningCount }}</h5>
            </div>
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-done">
              <i class="fas fa-check-circle"></i>
            </div>
            <div>
              <p class="stat-label">Passed (24h)</p>
              <h5 class="stat-value">{{ summary.passed24h }}</h5>
            </div>
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-overdue">
              <i class="fas fa-times-circle"></i>
            </div>
            <div>
              <p class="stat-label">Failed (24h)</p>
              <h5 class="stat-value">{{ summary.failed24h }}</h5>
            </div>
          </div>
        </div>
      </div>

      <div class="card mb-3 auto-shell-card">
        <div class="card-header pb-0">
          <div class="tab-shell" role="tablist" aria-label="Automation views">
            <div
              class="tab-item"
              :class="{ active: activeTab === 'repos' }"
              role="tab"
              @click="activeTab = 'repos'"
            >
              Repositories
            </div>
            <div
              class="tab-item"
              :class="{ active: activeTab === 'runs' }"
              role="tab"
              @click="activeTab = 'runs'"
            >
              Run history
            </div>
          </div>
        </div>

        <div class="card-body auto-shell-content">
          <!-- Repositories -->
          <div v-if="activeTab === 'repos'">
            <div v-if="repos.length === 0" class="text-center py-4 text-secondary text-sm">
              No repositories configured yet.
            </div>
            <div v-else class="auto-repo-grid">
              <article
                v-for="repo in repos"
                :key="repo.repoId"
                class="auto-repo-card"
              >
                <header class="auto-repo-card__header">
                  <div class="auto-repo-card__title">
                    <h6 class="auto-repo-name">{{ repo.displayName }}</h6>
                    <p class="auto-repo-github" :title="repo.fullName">
                      <i class="fab fa-github" aria-hidden="true"></i>
                      <span>{{ repo.fullName }}</span>
                    </p>
                  </div>
                  <span class="framework-pill" :class="frameworkPillClass(repo.framework)">
                    {{ frameworkLabel(repo.framework) }}
                  </span>
                </header>

                <p v-if="repo.description" class="auto-repo-card__desc">{{ repo.description }}</p>

                <dl class="auto-repo-meta">
                  <div class="auto-repo-meta__item">
                    <dt>Workflow</dt>
                    <dd :title="repo.workflowName || ''">{{ repo.workflowName || '—' }}</dd>
                  </div>
                  <div class="auto-repo-meta__item">
                    <dt>Default branch</dt>
                    <dd>
                      <i class="fas fa-code-branch auto-repo-branch-icon" aria-hidden="true"></i>
                      {{ repo.defaultBranch || '—' }}
                    </dd>
                  </div>
                </dl>

                <footer class="auto-repo-card__footer">
                  <div class="auto-repo-last">
                    <template v-if="repo.lastRunStatus">
                      <div class="auto-repo-last__status-row">
                        <span class="status-pill" :class="statusPillClass(repo.lastRunStatus)">
                          {{ statusLabel(repo.lastRunStatus) }}
                        </span>
                        <span
                          v-if="repo.lastRunEnvironment"
                          class="auto-env-pill"
                          :title="'Last run environment: ' + environmentLabel(repo.lastRunEnvironment)"
                        >
                          {{ environmentLabel(repo.lastRunEnvironment) }}
                        </span>
                      </div>
                      <span v-if="repo.lastRunAt" class="auto-repo-last__time">
                        {{ formatDateTime(repo.lastRunAt) }}
                      </span>
                    </template>
                    <span v-else class="auto-repo-last__empty">No runs yet</span>
                  </div>
                  <p
                    v-if="runFeedback && runFeedback.repoId === repo.repoId"
                    class="auto-repo-feedback mb-0"
                    :class="runFeedback.type === 'success' ? 'text-success' : 'text-danger'"
                  >
                    {{ runFeedback.message }}
                  </p>
                  <div class="auto-repo-run-row">
                    <div class="auto-repo-env">
                      <label class="auto-repo-env__label">Environment</label>
                      <base-select
                        size="sm"
                        :model-value="repoEnvFor(repo.repoId)"
                        :options="environmentOptions"
                        option-label="label"
                        option-value="value"
                        :disabled="triggeringRepoId === repo.repoId"
                        @update:model-value="setRepoEnv(repo.repoId, $event)"
                      />
                    </div>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary mb-0 auto-repo-run-btn"
                      :disabled="triggeringRepoId === repo.repoId"
                      @click="triggerRunFromCard(repo)"
                    >
                      <i v-if="triggeringRepoId === repo.repoId" class="fas fa-spinner fa-spin me-1"></i>
                      <i v-else class="fas fa-play me-1"></i>
                      Run
                    </button>
                  </div>
                </footer>
              </article>
            </div>
          </div>

          <!-- Run history -->
          <div v-else>
            <div class="auto-filters-row mb-3">
              <div class="stat-col stat-col-filter">
                <div class="stat-card filter-card">
                  <div class="filter-content">
                    <p class="stat-label">Repository</p>
                    <base-select
                      size="sm"
                      :model-value="filterRepoId"
                      :options="repoFilterOptions"
                      option-label="label"
                      option-value="value"
                      placeholder="All repositories"
                      @update:model-value="filterRepoId = $event; loadRuns()"
                    />
                  </div>
                </div>
              </div>
              <div class="stat-col stat-col-filter">
                <div class="stat-card filter-card sprint-card">
                  <div class="filter-content">
                    <p class="stat-label">Status</p>
                    <base-select
                      size="sm"
                      :model-value="filterStatus"
                      :options="statusFilterOptions"
                      option-label="label"
                      option-value="value"
                      @update:model-value="filterStatus = $event; loadRuns()"
                    />
                  </div>
                </div>
              </div>
              <div class="stat-col stat-col-filter">
                <div class="stat-card filter-card">
                  <div class="filter-content">
                    <p class="stat-label">Framework</p>
                    <base-select
                      size="sm"
                      :model-value="filterFramework"
                      :options="frameworkFilterOptions"
                      option-label="label"
                      option-value="value"
                      @update:model-value="filterFramework = $event; loadRuns()"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div v-if="runsLoading" class="text-center py-4 text-secondary text-sm">
              <i class="fas fa-spinner fa-spin me-1"></i> Loading runs...
            </div>
            <div v-else-if="runs.length === 0" class="text-center py-4 text-secondary text-sm">
              No runs match the selected filters.
            </div>
            <div v-else class="pm-table">
              <div class="pm-table-header">
                <span class="col-chevron"></span>
                <span class="col-repo">Repository</span>
                <span class="col-branch">Branch</span>
                <span class="col-status text-center">Status</span>
                <span class="col-progress text-center">Progress</span>
                <span class="col-result text-center">Result</span>
                <span class="col-when text-center">Started</span>
                <span class="col-action text-center">Action</span>
              </div>
              <div
                v-for="run in runs"
                :key="run.runId"
                class="auto-run-block"
                :class="{ 'auto-run-block--expanded': expandedRunId === run.runId }"
              >
                <div
                  class="pm-row auto-run-row"
                  role="button"
                  tabindex="0"
                  :aria-expanded="expandedRunId === run.runId ? 'true' : 'false'"
                  @click="toggleRunExpand(run)"
                  @keydown.enter="toggleRunExpand(run)"
                >
                  <div class="col-chevron">
                    <i
                      class="fas text-secondary"
                      :class="expandedRunId === run.runId ? 'fa-chevron-down' : 'fa-chevron-right'"
                    ></i>
                  </div>
                  <div class="col-repo">
                    <span class="auto-run-repo-name">{{ run.displayName }}</span>
                    <span class="framework-pill framework-pill--sm ms-1" :class="frameworkPillClass(run.framework)">
                      {{ frameworkLabel(run.framework) }}
                    </span>
                    <p class="text-xs text-secondary mb-0 mt-1">{{ run.fullName }}</p>
                  </div>
                  <div class="col-branch">
                    <span class="text-xs font-weight-bold">{{ run.branch || '—' }}</span>
                  </div>
                  <div class="col-status text-center">
                    <span class="status-pill" :class="statusPillClass(run.status)">{{ statusLabel(run.status) }}</span>
                    <span
                      v-if="run.environment && (run.status === 'running' || run.status === 'queued')"
                      class="auto-run-env-tag"
                    >
                      {{ environmentLabel(run.environment) }}
                    </span>
                  </div>
                  <div class="col-progress text-center">
                    <div class="auto-progress-wrap">
                      <div class="auto-progress-track">
                        <div
                          class="auto-progress-bar"
                          :class="progressBarClass(run)"
                          :style="{ width: Math.min(100, run.progressPct || 0) + '%' }"
                        ></div>
                      </div>
                      <span class="text-xs font-weight-bold">{{ run.progressPct || 0 }}%</span>
                    </div>
                  </div>
                  <div class="col-result text-center">
                    <span class="text-xs">
                      <span class="text-success font-weight-bold">{{ run.passed }}</span>
                      /
                      <span class="text-danger font-weight-bold">{{ run.failed }}</span>
                      <span v-if="run.skipped" class="text-secondary"> ({{ run.skipped }} skip)</span>
                    </span>
                  </div>
                  <div class="col-when text-center">
                    <span class="text-xs text-secondary">{{ formatDateTime(run.startedAt || run.createdAt) }}</span>
                  </div>
                  <div class="col-action text-center" @click.stop>
                    <a
                      v-if="run.reportUrl"
                      :href="run.reportUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="btn btn-link btn-sm text-info mb-0 p-0"
                      title="Open in CI"
                    >
                      <i class="fas fa-external-link-alt"></i>
                    </a>
                  </div>
                </div>

                <div v-show="expandedRunId === run.runId" class="auto-run-expand-panel">
                  <div class="auto-run-expand-inner">
                    <div class="row g-3 mb-3">
                      <div class="col-sm-6 col-md-3">
                        <p class="text-xs text-secondary mb-0">Framework</p>
                        <p class="text-sm font-weight-bold mb-0">{{ frameworkLabel(runDetailFor(run)?.framework) }}</p>
                      </div>
                      <div class="col-sm-6 col-md-3">
                        <p class="text-xs text-secondary mb-0">Environment</p>
                        <p class="text-sm font-weight-bold mb-0">{{ runDetailFor(run)?.environment || '—' }}</p>
                      </div>
                      <div class="col-sm-6 col-md-3">
                        <p class="text-xs text-secondary mb-0">Triggered by</p>
                        <p class="text-sm font-weight-bold mb-0">{{ runDetailFor(run)?.triggeredBy || '—' }}</p>
                      </div>
                      <div class="col-sm-6 col-md-3">
                        <p class="text-xs text-secondary mb-0">Finished</p>
                        <p class="text-sm font-weight-bold mb-0">
                          {{ formatDateTime(runDetailFor(run)?.finishedAt) }}
                        </p>
                      </div>
                    </div>
                    <div class="auto-detail-progress mb-3">
                      <div class="d-flex justify-content-between mb-1">
                        <span class="text-xs text-secondary">Progress</span>
                        <span class="text-xs font-weight-bold">{{ runDetailFor(run)?.progressPct || 0 }}%</span>
                      </div>
                      <div class="auto-progress-track auto-progress-track--lg">
                        <div
                          class="auto-progress-bar"
                          :class="progressBarClass(runDetailFor(run))"
                          :style="{ width: Math.min(100, runDetailFor(run)?.progressPct || 0) + '%' }"
                        ></div>
                      </div>
                    </div>
                    <div class="stats-row mb-0">
                      <div class="stat-col">
                        <div class="stat-card py-2 px-2">
                          <p class="stat-label mb-0">Total</p>
                          <h6 class="stat-value mb-0">{{ runDetailFor(run)?.totalTests || 0 }}</h6>
                        </div>
                      </div>
                      <div class="stat-col">
                        <div class="stat-card py-2 px-2">
                          <p class="stat-label mb-0">Passed</p>
                          <h6 class="stat-value mb-0 text-success">{{ runDetailFor(run)?.passed || 0 }}</h6>
                        </div>
                      </div>
                      <div class="stat-col">
                        <div class="stat-card py-2 px-2">
                          <p class="stat-label mb-0">Failed</p>
                          <h6 class="stat-value mb-0 text-danger">{{ runDetailFor(run)?.failed || 0 }}</h6>
                        </div>
                      </div>
                      <div class="stat-col">
                        <div class="stat-card py-2 px-2">
                          <p class="stat-label mb-0">Skipped</p>
                          <h6 class="stat-value mb-0">{{ runDetailFor(run)?.skipped || 0 }}</h6>
                        </div>
                      </div>
                    </div>
                    <div v-if="runDetailFor(run)?.reportUrl" class="mt-3 text-end">
                      <a
                        :href="runDetailFor(run).reportUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="btn btn-sm bg-gradient-primary text-white mb-0"
                      >
                        <i class="fas fa-external-link-alt me-1"></i>Open in CI
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import BaseSelect from "@/components/BaseSelect.vue";
import { automationApi } from "@/services/backendApi";

export default {
  name: "automation-tracking",
  components: { BaseSelect },
  data() {
    return {
      loading: true,
      runsLoading: false,
      loadError: null,
      activeTab: "repos",
      summary: { repoCount: 0, runningCount: 0, passed24h: 0, failed24h: 0 },
      repos: [],
      runs: [],
      filterRepoId: "all",
      filterStatus: "all",
      filterFramework: "all",
      triggeringRepoId: null,
      expandedRunId: null,
      expandedRunDetail: null,
      repoEnvById: {},
      runFeedback: null,
      defaultEnvironment: "dev",
      environmentOptions: [
        { value: "dev", label: "Dev" },
        { value: "staging", label: "Staging" },
        { value: "uat", label: "Uat" },
        { value: "prod", label: "Prod" },
      ],
      statusFilterOptions: [
        { value: "all", label: "All statuses" },
        { value: "queued", label: "Queued" },
        { value: "running", label: "Running" },
        { value: "success", label: "Success" },
        { value: "failed", label: "Failed" },
        { value: "cancelled", label: "Cancelled" },
      ],
      frameworkFilterOptions: [
        { value: "all", label: "All frameworks" },
        { value: "playwright", label: "Playwright" },
        { value: "cypress", label: "Cypress" },
        { value: "appium", label: "Appium" },
      ],
    };
  },
  computed: {
    repoFilterOptions() {
      const items = (this.repos || []).map((r) => ({
        value: String(r.repoId),
        label: r.displayName,
      }));
      return [{ value: "all", label: "All repositories" }, ...items];
    },
  },
  created() {
    this.loadAll();
  },
  mounted() {
    window.addEventListener("keydown", this.onEscapeKey);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onEscapeKey);
  },
  methods: {
    onEscapeKey(e) {
      if (e.key !== "Escape") return;
      if (this.expandedRunId != null) {
        this.expandedRunId = null;
        this.expandedRunDetail = null;
      }
    },
    async loadAll() {
      this.loading = true;
      this.loadError = null;
      try {
        const [summary, repos, envRes] = await Promise.all([
          automationApi.getSummary(),
          automationApi.getRepos(),
          automationApi.getEnvironments().catch(() => null),
        ]);
        this.summary = summary || this.summary;
        this.repos = Array.isArray(repos) ? repos : [];
        if (Array.isArray(envRes?.environments) && envRes.environments.length) {
          this.environmentOptions = envRes.environments;
          this.defaultEnvironment = envRes.environments[0].value;
        }
        this.initRepoEnvSelections();
        await this.loadRuns();
      } catch (e) {
        this.loadError = e?.message || "Failed to load automation data";
        this.repos = [];
        this.runs = [];
      } finally {
        this.loading = false;
      }
    },
    async loadRuns() {
      this.runsLoading = true;
      try {
        const params = { limit: 80 };
        if (this.filterRepoId && this.filterRepoId !== "all") params.repoId = this.filterRepoId;
        if (this.filterStatus && this.filterStatus !== "all") params.status = this.filterStatus;
        if (this.filterFramework && this.filterFramework !== "all") params.framework = this.filterFramework;
        this.runs = await automationApi.getRuns(params);
      } catch (e) {
        console.error("loadRuns:", e);
        this.runs = [];
      } finally {
        this.runsLoading = false;
      }
    },
    repoEnvStorageKey() {
      return "testops-automation-repo-env";
    },
    loadRepoEnvFromStorage() {
      try {
        return JSON.parse(localStorage.getItem(this.repoEnvStorageKey()) || "{}");
      } catch {
        return {};
      }
    },
    persistRepoEnv(repoId, env) {
      try {
        const all = this.loadRepoEnvFromStorage();
        all[String(repoId)] = env;
        localStorage.setItem(this.repoEnvStorageKey(), JSON.stringify(all));
      } catch {
        /* ignore */
      }
    },
    repoEnvFor(repoId) {
      return this.repoEnvById[repoId] || this.defaultEnvironment;
    },
    setRepoEnv(repoId, env) {
      if (!env) return;
      this.repoEnvById = { ...this.repoEnvById, [repoId]: env };
      this.persistRepoEnv(repoId, env);
    },
    initRepoEnvSelections() {
      const stored = this.loadRepoEnvFromStorage();
      const next = { ...this.repoEnvById };
      for (const repo of this.repos || []) {
        if (next[repo.repoId]) continue;
        const saved = stored[String(repo.repoId)];
        const valid = (this.environmentOptions || []).some((o) => o.value === saved);
        next[repo.repoId] = valid ? saved : this.defaultEnvironment;
      }
      this.repoEnvById = next;
    },
    async triggerRunFromCard(repo) {
      if (!repo?.repoId) return;
      this.triggeringRepoId = repo.repoId;
      this.runFeedback = null;
      try {
        const env = this.repoEnvFor(repo.repoId);
        const res = await automationApi.triggerRun({
          repoId: repo.repoId,
          branch: repo.defaultBranch || "main",
          environment: env,
        });
        this.runFeedback = {
          repoId: repo.repoId,
          type: "success",
          message: res?.message || `Queued on ${env}.`,
        };
        await this.loadAll();
        setTimeout(() => {
          if (this.runFeedback?.repoId === repo.repoId) this.runFeedback = null;
        }, 4000);
      } catch (e) {
        this.runFeedback = {
          repoId: repo.repoId,
          type: "error",
          message: e?.message || "Failed to queue run",
        };
      } finally {
        this.triggeringRepoId = null;
      }
    },
    runDetailFor(run) {
      if (this.expandedRunId === run.runId && this.expandedRunDetail) {
        return this.expandedRunDetail;
      }
      return run;
    },
    async toggleRunExpand(run) {
      if (this.expandedRunId === run.runId) {
        this.expandedRunId = null;
        this.expandedRunDetail = null;
        return;
      }
      this.expandedRunId = run.runId;
      this.expandedRunDetail = run;
      try {
        const fresh = await automationApi.getRunById(run.runId);
        if (fresh && this.expandedRunId === run.runId) {
          this.expandedRunDetail = fresh;
        }
      } catch (_) {
        /* keep list row */
      }
    },
    frameworkLabel(fw) {
      const k = String(fw || "").toLowerCase();
      if (k === "playwright") return "Playwright";
      if (k === "cypress") return "Cypress";
      if (k === "appium") return "Appium";
      return fw || "—";
    },
    environmentLabel(value) {
      const key = String(value || "").trim().toLowerCase();
      if (!key) return "—";
      const opt = (this.environmentOptions || []).find((o) => String(o.value).toLowerCase() === key);
      if (opt?.label) return opt.label;
      return key.charAt(0).toUpperCase() + key.slice(1);
    },
    frameworkPillClass(fw) {
      const k = String(fw || "").toLowerCase();
      if (k === "playwright") return "framework-pill--pw";
      if (k === "cypress") return "framework-pill--cy";
      if (k === "appium") return "framework-pill--ap";
      return "";
    },
    statusLabel(status) {
      const s = String(status || "").toLowerCase();
      const map = {
        queued: "Queued",
        running: "Running",
        success: "Success",
        failed: "Failed",
        cancelled: "Cancelled",
      };
      return map[s] || status || "—";
    },
    statusPillClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "success") return "pill-done";
      if (s === "failed") return "pill-failed";
      if (s === "running") return "pill-inprogress";
      if (s === "queued") return "pill-draft";
      return "pill-inactive";
    },
    progressBarClass(run) {
      const s = String(run?.status || "").toLowerCase();
      if (s === "failed") return "auto-progress-bar--fail";
      if (s === "success") return "auto-progress-bar--ok";
      return "auto-progress-bar--run";
    },
    formatDateTime(iso) {
      if (!iso) return "—";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "—";
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
  },
};
</script>

<style scoped>
.page-title {
  color: #344767;
}
.stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}
.stat-col {
  flex: 1 1 140px;
  min-width: 120px;
}
.stat-col-filter {
  flex: 1 1 200px;
  min-width: 180px;
}
.stat-card {
  background: var(--surface-card, #fff);
  border: 1px solid var(--surface-border, #e9ecef);
  border-radius: 12px;
  padding: 0.75rem 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  box-shadow: var(--shadow-card, 0 2px 8px rgba(0, 0, 0, 0.06));
  height: 100%;
}
.filter-card {
  background: linear-gradient(135deg, #f8faff 0%, #eef2ff 100%);
  border-color: #d6dcf5;
}
.sprint-card {
  background: linear-gradient(135deg, #f0f9fc 0%, #e0f3fa 100%);
  border-color: #b8e3f0;
}
.stat-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8392ab;
  margin-bottom: 0.1rem;
}
.stat-value {
  font-size: 1.15rem;
  font-weight: 700;
  color: #344767;
  margin-bottom: 0;
}
.stat-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.9rem;
  flex-shrink: 0;
}
.stat-icon-total {
  background: linear-gradient(195deg, #42424a, #191919);
}
.stat-icon-inprogress {
  background: linear-gradient(195deg, #17c1e8, #0c7a9a);
}
.stat-icon-done {
  background: linear-gradient(195deg, #82d616, #17ad37);
}
.stat-icon-overdue {
  background: linear-gradient(195deg, #ef5350, #e53935);
}
.auto-shell-card {
  border-radius: 0.75rem;
}
.auto-shell-card .card-header {
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
}
.auto-shell-content {
  padding-top: 1rem;
}
.tab-shell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.3rem;
}
.tab-item {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #696969;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.55rem 0.9rem;
  border-radius: 12px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.2s ease;
}
.tab-item.active {
  color: #fff;
  background: #344767;
  box-shadow: 0 6px 14px rgba(52, 71, 103, 0.28);
}
.auto-filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}
.auto-repo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
}
.auto-repo-card {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1.15rem 1.2rem;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.15s ease;
  min-height: 0;
}
.auto-repo-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}
.auto-repo-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}
.auto-repo-card__title {
  flex: 1;
  min-width: 0;
}
.auto-repo-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #344767;
  margin: 0 0 0.35rem;
  line-height: 1.3;
}
.auto-repo-github {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0;
  font-size: 0.72rem;
  color: #8392ab;
  line-height: 1.35;
}
.auto-repo-github span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.auto-repo-card__desc {
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: #67748e;
}
.auto-repo-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
}
.auto-repo-meta__item {
  margin: 0;
  padding: 0.55rem 0.65rem;
  background: #f8f9fa;
  border: 1px solid #eef1f4;
  border-radius: 8px;
  min-width: 0;
}
.auto-repo-meta__item dt {
  margin: 0 0 0.2rem;
  font-size: 0.62rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8392ab;
}
.auto-repo-meta__item dd {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: #344767;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.auto-repo-branch-icon {
  font-size: 0.65rem;
  margin-right: 0.2rem;
  color: #8392ab;
}
.auto-repo-card__footer {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: auto;
  padding-top: 0.85rem;
  border-top: 1px solid #eef1f4;
}
.auto-repo-run-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.65rem;
  flex-wrap: wrap;
}
.auto-repo-env {
  flex: 1;
  min-width: 120px;
  max-width: 160px;
}
.auto-repo-env__label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.62rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8392ab;
}
.auto-repo-env .base-select {
  width: 100%;
}
.auto-repo-run-btn {
  flex-shrink: 0;
}
.auto-repo-last {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  min-width: 0;
}
.auto-repo-last__status-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
}
.auto-env-pill {
  display: inline-block;
  font-size: 0.62rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.15rem 0.45rem;
  border-radius: 6px;
  background: #eef2f7;
  color: #495057;
  border: 1px solid #dee2e6;
}
.auto-run-env-tag {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8392ab;
}
.auto-repo-last__time {
  font-size: 0.68rem;
  color: #8392ab;
  line-height: 1.2;
}
.auto-repo-last__empty {
  font-size: 0.72rem;
  color: #8392ab;
}
.auto-repo-feedback {
  font-size: 0.72rem;
  line-height: 1.35;
}
@media (max-width: 575px) {
  .auto-repo-meta {
    grid-template-columns: 1fr;
  }
  .auto-repo-run-row {
    flex-direction: column;
    align-items: stretch;
  }
  .auto-repo-env {
    max-width: none;
  }
  .auto-repo-run-btn {
    width: 100%;
  }
}
.framework-pill {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.2rem 0.45rem;
  border-radius: 6px;
  white-space: nowrap;
}
.framework-pill--sm {
  font-size: 0.58rem;
  padding: 0.15rem 0.35rem;
}
.framework-pill--pw {
  background: #e8f4fd;
  color: #0d6efd;
  border: 1px solid #b6d4fe;
}
.framework-pill--cy {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}
.framework-pill--ap {
  background: #f3e5f5;
  color: #7b1fa2;
  border: 1px solid #ce93d8;
}
.pm-table {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}
.pm-table-header {
  display: flex;
  align-items: center;
  padding: 0.65rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8392ab;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.auto-run-block {
  border-bottom: 1px solid #f1f3f5;
}
.auto-run-block:last-child {
  border-bottom: none;
}
.auto-run-block--expanded {
  background: #fafbfc;
}
.auto-run-block--expanded .pm-row {
  background: #f6f8fb;
}
.pm-row {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.15s;
}
.pm-row:hover {
  background: #fafbfc;
}
.col-chevron {
  flex: 0 0 28px;
  min-width: 28px;
  text-align: center;
  font-size: 0.65rem;
}
.auto-run-expand-panel {
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}
.auto-run-expand-inner {
  padding: 1rem 1rem 1rem 2.75rem;
}
.col-repo {
  flex: 2.2;
  min-width: 160px;
}
.col-branch {
  flex: 1;
  min-width: 80px;
}
.col-status {
  flex: 0.9;
  min-width: 90px;
}
.col-progress {
  flex: 1.2;
  min-width: 110px;
}
.col-result {
  flex: 1;
  min-width: 90px;
}
.col-when {
  flex: 1.1;
  min-width: 100px;
}
.col-action {
  flex: 0.5;
  min-width: 48px;
}
.auto-run-repo-name {
  font-size: 0.82rem;
  font-weight: 700;
  color: #344767;
}
.auto-progress-wrap {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  justify-content: center;
}
.auto-progress-track {
  flex: 1;
  max-width: 72px;
  height: 6px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}
.auto-progress-track--lg {
  max-width: 100%;
  height: 8px;
}
.auto-progress-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}
.auto-progress-bar--ok {
  background: linear-gradient(90deg, #82d616, #17ad37);
}
.auto-progress-bar--fail {
  background: linear-gradient(90deg, #ef5350, #e53935);
}
.auto-progress-bar--run {
  background: linear-gradient(90deg, #17c1e8, #0c7a9a);
}
</style>
